'use client';

import type { RefObject } from 'react';
import { useEffect, useState } from 'react';

import { cn } from '@/app/utils/cn';

export type MountTransitionState = 'initial' | 'animate' | 'exit';

interface MountTransition {
  /**
   * A boolean indicating whether the component should be visible.
   */
  isMount: boolean;
  /**
   * A React ref object attached to the transitioning element.
   */
  ref: RefObject<HTMLElement | null>;
  /**
   * The base CSS class applied during the transition.
   */
  baseClassName?: string;
  /**
   * The CSS class applied before the component enter (before visible).
   */
  initialClassName?: string;
  /**
   * The CSS class applied when the component enters (becomes visible).
   */
  animateClassName?: string;
  /**
   * The CSS class applied when the component exits (becomes hidden).
   */
  exitClassName?: string;
  /**
   * To determince the exact transition element by ID when the children of this element also has transtion that can lead to conflict.
   */
  targetId?: string;
}

type MountTransitionReturnType = {
  /**
   * Indicates whether the component is currently transitioning
   */
  isTransitioning: boolean;
  /**
   * The current state of the transition ('initial', 'animate', or 'exit')
   */
  transitionState: MountTransitionState;
  /**
   * The CSS class name to apply to the transitioning element
   */
  className: string;
};

/**
 * Custom React hook for managing mount/unmount transitions with specific CSS(tailwind) classes.
 *
 * @param {boolean} options.isMount - A boolean indicating whether the component should be visible.
 * @param {React.RefObject<HTMLElement>} options.ref - A React ref object attached to the transitioning element.
 * @param {string} options.baseClassName - The base CSS class applied during the transition.
 * @param {string} options.initialClassName - TThe CSS class applied before the component enter (before visible).
 * @param {string} options.animateClassName - The CSS class applied when the component enters (becomes visible).
 * @param {string} options.exitClassName - The CSS class applied when the component exits (becomes hidden).
 *
 * @returns {boolean} return.isTransitioning - Indicates whether the component is currently transitioning.
 * @returns {MountTransitionState} return.transitionState - The current state of the transition ('initial', 'animate', or 'exit').
 * @returns {string} return.className - The CSS class name to apply to the transitioning element.
 *
 * Read more: [Storybook docs: useMountTransition](https://tis-web-story-book.netlify.app/?path=/docs/hooks-usemounttransition--documentation)
 */
export const useMountTransition = ({
  isMount = false,
  ref,
  baseClassName = '',
  initialClassName = '',
  animateClassName = '',
  exitClassName = '',
  targetId,
}: MountTransition): MountTransitionReturnType => {
  const [isTransitioning, setIsTransitioning] = useState<boolean>(false);
  const [transitionState, setTransitionState] =
    useState<MountTransitionState>('initial');

  useEffect(() => {
    let rafId: number | undefined;
    const element = ref.current;

    const handleTransitionEnd = (event: TransitionEvent) => {
      const transitionTarget = event.target as HTMLElement | null;
      if (!transitionTarget) return;
      const isTargetEnd = targetId
        ? transitionTarget.id === targetId
        : transitionTarget === element;
      if (!isTargetEnd) return;

      cleanup();
      setIsTransitioning(false);
      setTransitionState('initial');
    };

    const cleanup = () => {
      if (rafId !== undefined) {
        cancelAnimationFrame(rafId);
      }
      element?.removeEventListener('transitionend', handleTransitionEnd);
    };

    cleanup();

    if (isMount) {
      rafId = requestAnimationFrame(() => {
        setIsTransitioning(true);
        setTransitionState('initial');

        rafId = requestAnimationFrame(() => {
          setTransitionState('animate');
        });
      });
      return cleanup;
    }

    if (element) {
      rafId = requestAnimationFrame(() => {
        setIsTransitioning(true);
        setTransitionState('exit');
      });
      element.addEventListener('transitionend', handleTransitionEnd);
    } else {
      rafId = requestAnimationFrame(() => {
        setIsTransitioning(false);
        setTransitionState('initial');
      });
    }

    return cleanup;
  }, [isMount, ref, targetId]);

  return {
    isTransitioning,
    transitionState,
    className: cn(baseClassName, {
      [initialClassName]: transitionState === 'initial',
      [animateClassName]: transitionState === 'animate',
      [exitClassName]: transitionState === 'exit',
    }),
  };
};
